import React from 'react';
import { PreviewRow, PreviewStatus, DbSchemaColumn } from '../../../types/import'; // Adjust path as needed
import { CheckCircle, AlertTriangle, RefreshCw, Info, XCircle } from 'lucide-react';

interface PreviewTableProps {
  previewData: PreviewRow[];
  columns: DbSchemaColumn[]; // Pass available DB columns for header info
  identifierColumn: string; // To highlight the identifier column
}

// Helper to get status icon and color
const getStatusInfo = (status: PreviewStatus) => {
  switch (status) {
    case PreviewStatus.New:
      return { icon: <CheckCircle className="h-4 w-4 text-green-500" />, text: 'Novo', color: 'bg-green-50' };
    case PreviewStatus.Update:
      return { icon: <RefreshCw className="h-4 w-4 text-blue-500" />, text: 'Atualizar', color: 'bg-blue-50' };
    case PreviewStatus.Identical:
      return { icon: <Info className="h-4 w-4 text-gray-500" />, text: 'Idêntico', color: 'bg-gray-50' };
    case PreviewStatus.Error:
      return { icon: <XCircle className="h-4 w-4 text-red-500" />, text: 'Erro', color: 'bg-red-50' };
    case PreviewStatus.Skipped:
       return { icon: <AlertTriangle className="h-4 w-4 text-yellow-500" />, text: 'Ignorado', color: 'bg-yellow-50' };
    default:
      return { icon: <Info className="h-4 w-4 text-gray-400" />, text: 'Desconhecido', color: 'bg-gray-50' };
  }
};

// Helper to render cell content and highlight changes
const renderCell = (row: PreviewRow, dbColumn: string) => {
    const newValue = row.csvData?.[dbColumn];
    const oldValue = row.dbData?.[dbColumn];
    const hasChange = row.changes?.[dbColumn] !== undefined;

    if (row.status === PreviewStatus.New) {
        return <span className="text-green-700">{String(newValue ?? '')}</span>;
    }

    if (row.status === PreviewStatus.Update && hasChange) {
        return (
            <>
                <span className="line-through text-red-500 mr-1">{String(oldValue ?? '')}</span>
                <span className="text-blue-700 font-semibold">{String(newValue ?? '')}</span>
            </>
        );
    }
    
    // For Identical or unchanged fields in Update rows
    return <span className="text-gray-600">{String(oldValue ?? newValue ?? '')}</span>;
};


export const PreviewTable: React.FC<PreviewTableProps> = ({
  previewData,
  columns,
  identifierColumn,
}) => {
  if (!previewData || previewData.length === 0) {
    return <p className="text-sm text-gray-500 italic mt-4">Nenhum dado para pré-visualizar.</p>;
  }

  // Determine the columns to display based on what's present in csvData/dbData
  // Get all unique keys from csvData and dbData across all rows, excluding the identifier
   const displayColumns = Array.from(new Set(
       previewData.flatMap(row => [
          ...Object.keys(row.csvData || {}),
          ...Object.keys(row.dbData || {})
       ])
   )).filter(col => col !== identifierColumn);

  // Sort columns based on the original DB schema order if possible
  const sortedDisplayColumns = displayColumns.sort((a, b) => {
      const colA = columns.find(c => c.column_name === a);
      const colB = columns.find(c => c.column_name === b);
      if (colA && colB) {
          return colA.ordinal_position - colB.ordinal_position;
      }
      return 0; // Keep original order if columns not found in schema
  });

  return (
    <div className="mt-4 overflow-x-auto">
       <h3 className="text-md font-semibold text-gray-700 mb-2">Resultados da Pré-visualização ({previewData.length} linhas):</h3>
       <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
         <thead className="bg-gray-100">
           <tr>
             <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-100 z-10">Status</th>
             <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">{identifierColumn} (ID)</th>
             {/* Dynamically generate headers for mapped columns */}
             {sortedDisplayColumns.map(colName => (
               <th key={colName} scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">{colName}</th>
             ))}
             <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Erro</th>
           </tr>
         </thead>
         <tbody className="bg-white divide-y divide-gray-200">
           {previewData.map((row, index) => {
              const statusInfo = getStatusInfo(row.status);
             return (
               <tr key={`${row.id}-${index}`} className={statusInfo.color}>
                 {/* Status Cell (Sticky) */}
                 <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 sticky left-0 z-10" style={{ backgroundColor: 'inherit' }}>
                    <span className='flex items-center space-x-1' title={statusInfo.text}>
                        {statusInfo.icon} 
                        {/* <span>{statusInfo.text}</span> */}
                    </span>
                 </td>
                 {/* Identifier Cell */}
                 <td className="px-3 py-2 whitespace-nowrap text-sm font-semibold text-gray-800">
                   {String(row.id ?? '')}
                 </td>
                 {/* Data Cells */}
                 {sortedDisplayColumns.map(colName => (
                   <td key={colName} className="px-3 py-2 whitespace-nowrap text-sm">
                     {renderCell(row, colName)}
                   </td>
                 ))}
                 {/* Error Cell */}
                 <td className="px-3 py-2 text-xs text-red-700 whitespace-normal">
                     {row.error || ''}
                 </td>
               </tr>
             );
            })}
         </tbody>
       </table>
    </div>
  );
}; 